import { useState } from 'react';
import { Banknote, ReceiptText, ShoppingBag, XCircle, type LucideIcon } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import type { StoreAnalyticsSeries, StoreSeriesTotals } from '@/client/types/stores/parity';
import { cn } from '@/lib/utils';

import { Skeleton } from '@/components/ui/skeleton';

import { money, pctChange } from './shared';

export const compact = (n: number) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

type MetricKey = 'revenue' | 'paid_orders' | 'average_order_value' | 'cancelled';

interface Metric {
  key: MetricKey;
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Line colour when this metric is on the chart. */
  color: string;
  /** Tailwind classes for the icon badge and the selected card's ring. */
  badge: string;
  ring: string;
  format: (n: number) => string;
  axis: (n: number) => string;
  lowerIsBetter?: boolean;
}

const METRICS: Metric[] = [
  { key: 'revenue', label: 'Revenue', hint: 'from paid orders', icon: Banknote, color: '#059669', badge: 'bg-emerald-100 text-emerald-700', ring: 'border-emerald-500 ring-emerald-500', format: (n) => money(n), axis: compact },
  { key: 'paid_orders', label: 'Paid orders', hint: 'orders paid in range', icon: ShoppingBag, color: '#2563eb', badge: 'bg-blue-100 text-blue-700', ring: 'border-blue-500 ring-blue-500', format: (n) => n.toLocaleString(), axis: compact },
  { key: 'average_order_value', label: 'Avg. order', hint: 'revenue per paid order', icon: ReceiptText, color: '#7c3aed', badge: 'bg-violet-100 text-violet-700', ring: 'border-violet-500 ring-violet-500', format: (n) => money(n), axis: compact },
  { key: 'cancelled', label: 'Cancelled', hint: 'orders cancelled', icon: XCircle, color: '#e11d48', badge: 'bg-rose-100 text-rose-700', ring: 'border-rose-500 ring-rose-500', format: (n) => n.toLocaleString(), axis: compact, lowerIsBetter: true },
];

/** "2026-09-14" / "2026-W37" / "2026-09" → a short label for the axis. */
const bucketLabel = (raw: string, bucket: StoreAnalyticsSeries['bucket']): string => {
  if (bucket === 'week') {
    const [, week] = raw.split('-W');
    return week ? `Wk ${Number(week)}` : raw;
  }
  const d = new Date(bucket === 'month' ? `${raw}-01T00:00:00` : `${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return bucket === 'month'
    ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const bucketTitle = (raw: string, bucket: StoreAnalyticsSeries['bucket']): string => {
  if (bucket === 'week') return `Week ${raw.split('-W')[1] ?? ''}, ${raw.slice(0, 4)}`;
  const d = new Date(bucket === 'month' ? `${raw}-01T00:00:00` : `${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return bucket === 'month'
    ? d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const MetricTab: React.FC<{
  metric: Metric;
  value: string;
  delta: number | null;
  active: boolean;
  onClick: () => void;
}> = ({ metric, value, delta, active, onClick }) => {
  const good = delta == null ? null : metric.lowerIsBetter ? delta <= 0 : delta >= 0;
  const Icon = metric.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'group flex min-w-0 flex-col justify-between rounded-xl border bg-white p-4 text-left shadow-sm transition-all',
        active ? cn('ring-1', metric.ring) : 'border-gray-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('flex h-9 w-9 items-center justify-center rounded-lg', metric.badge)}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
        {delta != null && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
              good ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700',
            )}
          >
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-3 min-w-0">
        <p className="text-xs font-medium text-gray-500">{metric.label}</p>
        <p className="mt-0.5 truncate text-xl font-bold tabular-nums text-gray-900" title={value}>{value}</p>
        <p className="mt-0.5 truncate text-[11px] text-gray-400">{delta != null ? 'vs previous period' : metric.hint}</p>
      </div>
    </button>
  );
};

const ChartTooltip: React.FC<TooltipProps<number, string> & { bucket: StoreAnalyticsSeries['bucket']; format: (n: number) => string; metricLabel: string; color: string }> = ({
  active,
  payload,
  bucket,
  format,
  metricLabel,
  color,
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as { date: string };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="text-gray-500">{bucketTitle(point.date, bucket)}</p>
      <p className="mt-0.5 flex items-center gap-1.5 font-semibold text-gray-900">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        {metricLabel}: {format(Number(payload[0].value ?? 0))}
      </p>
    </div>
  );
};

/** Same grid as the loaded chart: 2×2 metric cards + the chart card. */
export const SalesChartSkeleton: React.FC<{ height?: number; actions?: React.ReactNode }> = ({ height = 260, actions }) => (
  <div className="grid gap-4 lg:grid-cols-[400px_minmax(0,1fr)]" aria-busy="true" aria-label="Loading sales">
    <div className="grid auto-rows-fr grid-cols-2 gap-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="h-4 w-12 rounded-full" />
          </div>
          <div className="mt-3 space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        </div>
      ))}
    </div>
    <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-56" />
        </div>
        {actions}
      </div>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {[38, 52, 45, 60, 55, 72, 64, 80, 70, 86, 78, 92].map((h, i) => (
          <Skeleton key={i} className="flex-1 rounded-t-md rounded-b-none" style={{ height: `${h}%` }} />
        ))}
      </div>
    </section>
  </div>
);

/**
 * Store sales over time — Overview and Performance. One metric at a time (the
 * tiles are the switcher), so there is never a second y-axis: revenue and order
 * counts live on different scales and are compared through the tiles' deltas.
 */
export const SalesChart: React.FC<{
  series: StoreAnalyticsSeries | null | undefined;
  loading?: boolean;
  height?: number;
  /** Chart card heading; `actions` sits on its right (e.g. the range picker). */
  title?: string;
  description?: string;
  actions?: React.ReactNode;
}> = ({ series, loading, height = 260, title = 'Sales', description, actions }) => {
  const [metricKey, setMetricKey] = useState<MetricKey>('revenue');
  if (loading || !series) return <SalesChartSkeleton height={height} actions={actions} />;

  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];
  const totals: StoreSeriesTotals = series.totals;
  const previous: StoreSeriesTotals = series.previous;
  const data = series.series.map((p) => ({ date: p.date, value: Number(p[metric.key] ?? 0) }));

  return (
    <div className="grid gap-4 lg:grid-cols-[400px_minmax(0,1fr)]">
      {/* Stats column: four standalone cards (2×2) that also switch the chart;
          they stretch to the chart card's height. */}
      <div className="grid auto-rows-fr grid-cols-2 gap-3">
        {METRICS.map((m) => (
          <MetricTab
            key={m.key}
            metric={m}
            value={m.format(totals[m.key])}
            delta={pctChange(totals[m.key], previous[m.key])}
            active={m.key === metric.key}
            onClick={() => setMetricKey(m.key)}
          />
        ))}
      </div>

      {/* Chart column: its own card. */}
      <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title} · {metric.label}</h3>
            {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
          </div>
          {actions}
        </div>
      {data.length === 0 ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-400" style={{ minHeight: height }}>
          No orders in this range.
        </div>
      ) : (
        <div className="min-w-0" style={{ height }} role="img" aria-label={`${metric.label} per ${series.bucket}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`salesFill-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={metric.color} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={metric.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => bucketLabel(v, series.bucket)}
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={metric.axis}
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
                width={44}
                allowDecimals={metric.key === 'revenue' || metric.key === 'average_order_value'}
              />
              <Tooltip
                cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                content={<ChartTooltip bucket={series.bucket} format={metric.format} metricLabel={metric.label} color={metric.color} />}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={metric.color}
                strokeWidth={2}
                fill={`url(#salesFill-${metric.key})`}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: metric.color }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      </section>
    </div>
  );
};
